import { useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type {
  FlatfinderData,
  FlatfinderEstate,
  FlatfinderEstateInput,
  FlatfinderPerspective,
} from 'realitiq-utils/src/flatfinder';
import { SimpleConfirmModal } from '@/components/modals/new/simple-confirmation-modal';
import { Button } from '@/components/ui/button';
import {
  createFlatfinderEstate,
  FlatfinderRequestError,
  flatfinderRequest,
  getFlatfinder,
  initializeFlatfinder,
  saveFlatfinderLayout,
  saveFlatfinderSettings,
  updateFlatfinderEstate,
} from './api';
import { PolygonEditor } from './polygon-editor';
import { announceFlatfinderPublicUpdate } from './public-sync';
import { useLayoutHistory } from './use-layout-history';
import { FlatfinderApartmentPanel } from './workspace-apartment-panel';
import { DeleteFlatfinderModal } from './workspace-delete-modal';
import { EmptyFlatfinderEditor } from './workspace-empty-editor';
import {
  useCancelAssignmentOutsideAvailableAreas,
  useFlatfinderHistoryShortcuts,
} from './workspace-effects';
import { FlatfinderEstateModal } from './workspace-estate-modal';
import { FlatfinderWorkspaceHeader } from './workspace-header';
import { FlatfinderPerspectiveControls } from './workspace-perspective-controls';
import { FlatfinderPerspectiveModal } from './workspace-perspective-modal';
import { FlatfinderPreviewModal } from './workspace-preview-modal';
import { FlatfinderSettingsModal } from './workspace-settings-modal';

export function FlatfinderEditor({ initial }: { initial: FlatfinderData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [data, setData] = useState(initial);
  const history = useLayoutHistory(initial.perspectives);
  const [drawing, setDrawing] = useState(false);
  const [canvasGeneration, setCanvasGeneration] = useState(0);
  const blocker = useBlocker({
    shouldBlockFn: () => history.dirty || drawing,
    enableBeforeUnload: history.dirty || drawing,
    withResolver: true,
  });
  const [perspectiveId, setPerspectiveId] = useState(initial.perspectives[0]?.id ?? '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [perspectiveForm, setPerspectiveForm] = useState<'new' | FlatfinderPerspective | null>(
    null
  );
  const [estateForm, setEstateForm] = useState<'new' | string | null>(null);
  const [hoveredEstateId, setHoveredEstateId] = useState<string | null>(null);
  const [areaHoveredEstateId, setAreaHoveredEstateId] = useState<string | null>(null);
  const [assignmentVersion, setAssignmentVersion] = useState(0);
  const [assigningEstateId, setAssigningEstateId] = useState<string | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const [preview, setPreview] = useState(false);
  const [settings, setSettings] = useState(false);
  const [settingsSaveError, setSettingsSaveError] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [reloadConfirmation, setReloadConfirmation] = useState(false);
  const [discardDrawingConfirmation, setDiscardDrawingConfirmation] = useState(false);
  const [removePerspectiveConfirmation, setRemovePerspectiveConfirmation] =
    useState<FlatfinderPerspective | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const requestId = useRef(crypto.randomUUID());
  const actionAfterDiscardingDrawing = useRef<(() => void) | null>(null);
  const perspective =
    history.perspectives.find((item) => item.id === perspectiveId) ?? history.perspectives[0];
  const selected = perspective?.regions.find((region) => region.id === selectedId);
  const estate = data.estates.find((item) => item.id === selected?.estateId);
  const formEstate = data.estates.find((item) => item.id === estateForm);
  const canEditLayout = data.permissions.general && data.permissions.media;

  useCancelAssignmentOutsideAvailableAreas({
    assigningEstateId,
    perspective,
    setAssigningEstateId,
  });
  useFlatfinderHistoryShortcuts({
    blocked:
      busy ||
      assigningEstateId !== null ||
      preview ||
      perspectiveForm !== null ||
      estateForm !== null ||
      settings ||
      !canEditLayout,
    onRedo: history.redo,
    onUndo: history.undo,
  });

  function acceptServerData(next: FlatfinderData, resetLayoutHistory = true) {
    setData(next);
    if (resetLayoutHistory) history.reset(next.perspectives);
    announceFlatfinderPublicUpdate(next.publicId, next.revision);
    queryClient.setQueryData(['flatfinder', data.projectId], next);
    void queryClient.invalidateQueries({ queryKey: ['flatfinders'] });
    void queryClient.invalidateQueries({ queryKey: ['projects', 'estates', data.projectId] });
  }

  async function runWorkspaceAction(
    action: () => Promise<void>,
    onError?: (cause: unknown) => void
  ) {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      onError?.(cause);
      const message = cause instanceof Error ? cause.message : t('flatfinderWorkspaceActionFailed');
      if (cause instanceof FlatfinderRequestError && cause.status === 409) {
        toast.error(
          <div className="flex flex-col gap-2">
            <p>{message}</p>
            <Button variant="outline" onClick={() => setReloadConfirmation(true)}>
              {t('flatfinderWorkspaceReload')}
            </Button>
          </div>,
          { toastId: 'flatfinder-conflict', autoClose: false }
        );
      } else {
        toast.error(message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function saveLayoutIfNeeded(force = false) {
    if (drawing) throw new Error(t('flatfinderWorkspaceFinishFirst'));
    if (!force && !history.dirty) return data;
    const next = await saveFlatfinderLayout(data.projectId, data.revision, history.perspectives);
    acceptServerData(next);
    return next;
  }

  function replacePerspective(next: FlatfinderPerspective) {
    history.commit((items) => items.map((item) => (item.id === next.id ? next : item)));
  }

  function selectRegion(id: string | null) {
    setSelectedId(id);
    setEstateForm(null);
    if (id) setShowPanel(true);
  }

  function finishApartmentAssignment() {
    setSelectedId(null);
    setAssignmentVersion((value) => value + 1);
  }

  function assignSelectedRegionToEstate(estateId: string | null) {
    if (!perspective || !selected) return;
    replacePerspective({
      ...perspective,
      regions: perspective.regions.map((region) =>
        region.id === selected.id ? { ...region, estateId } : region
      ),
    });
    finishApartmentAssignment();
    if (estateId) toast.success(t('flatfinderWorkspaceApartmentAssigned'));
  }

  function saveEstate(input: FlatfinderEstateInput) {
    void runWorkspaceAction(async () => {
      if (estateForm === 'new') {
        if (!selected) throw new Error(t('flatfinderWorkspaceSelectFirst'));
        const saved = await saveLayoutIfNeeded();
        const next = await createFlatfinderEstate(
          data.projectId,
          selected.id,
          saved.revision,
          input,
          requestId.current
        );
        acceptServerData(next);
        finishApartmentAssignment();
      } else if (estateForm) {
        const next = await updateFlatfinderEstate(data.projectId, estateForm, input);
        acceptServerData(next, !history.dirty);
        void queryClient.invalidateQueries({ queryKey: ['estate', estateForm] });
      }
      setEstateForm(null);
      toast.success(t('flatfinderWorkspaceApartmentSaved'));
    });
  }

  function continueAfterDiscardingDrawing(action: () => void) {
    if (!drawing) {
      action();
      return;
    }
    actionAfterDiscardingDrawing.current = action;
    setDiscardDrawingConfirmation(true);
  }

  function selectPerspective(nextPerspectiveId: string) {
    continueAfterDiscardingDrawing(() => {
      setPerspectiveId(nextPerspectiveId);
      setSelectedId(null);
      setEstateForm(null);
    });
  }

  function editCurrentPerspective() {
    if (perspective) {
      continueAfterDiscardingDrawing(() => setPerspectiveForm(perspective));
    }
  }

  function requestCurrentPerspectiveRemoval() {
    if (perspective) {
      continueAfterDiscardingDrawing(() => setRemovePerspectiveConfirmation(perspective));
    }
  }

  function createPerspective() {
    continueAfterDiscardingDrawing(() => setPerspectiveForm('new'));
  }

  function savePerspective(next: FlatfinderPerspective) {
    history.commit((items) =>
      items.some((item) => item.id === next.id)
        ? items.map((item) => (item.id === next.id ? next : item))
        : [...items, { ...next, sortOrder: items.length }]
    );
    setPerspectiveId(next.id);
    setSelectedId(null);
    setPerspectiveForm(null);
  }

  function reloadLatestFlatfinder() {
    void runWorkspaceAction(async () => {
      acceptServerData(await getFlatfinder(data.projectId));
      toast.dismiss('flatfinder-conflict');
    });
  }

  function discardCurrentDrawing() {
    const action = actionAfterDiscardingDrawing.current;
    actionAfterDiscardingDrawing.current = null;
    setDrawing(false);
    setCanvasGeneration((value) => value + 1);
    action?.();
  }

  function removeConfirmedPerspective() {
    if (!removePerspectiveConfirmation) return;
    history.commit((items) => items.filter((item) => item.id !== removePerspectiveConfirmation.id));
    setSelectedId(null);
    setEstateForm(null);
  }

  function deleteFlatfinder() {
    void runWorkspaceAction(async () => {
      await flatfinderRequest<{ success: boolean }>(
        `/projects/${data.projectId}/flatfinder?revision=${data.revision}`,
        'DELETE'
      );
      setDeleteConfirmation(false);
      history.reset([]);
      setDrawing(false);
      await queryClient.invalidateQueries({ queryKey: ['flatfinders'] });
      toast.success(t('flatfinderWorkspaceDeleted'));
      await navigate({ to: '/flatfinders' });
    });
  }

  function saveSharingSettings(status: 'draft' | 'published', contact: string | null) {
    setSettingsSaveError(null);
    void runWorkspaceAction(
      async () => {
        const saved = await saveLayoutIfNeeded();
        acceptServerData(
          await saveFlatfinderSettings(data.projectId, saved.revision, status, contact)
        );
        toast.success(t('flatfinderWorkspaceSettingsSaved'));
      },
      (cause) =>
        setSettingsSaveError(
          cause instanceof FlatfinderRequestError
            ? cause.message
            : t('flatfinderWorkspaceSaveFailed')
        )
    );
  }

  function saveEditorLayout() {
    void runWorkspaceAction(async () => {
      await saveLayoutIfNeeded(true);
      toast.success(t('flatfinderWorkspaceLayoutSaved'));
    });
  }

  function assignConnectingEstateToRegion(regionId: string) {
    if (!assigningEstateId || !perspective || !canEditLayout || busy) return;
    replacePerspective({
      ...perspective,
      regions: perspective.regions.map((region) =>
        region.id === regionId && !region.estateId
          ? { ...region, estateId: assigningEstateId }
          : region
      ),
    });
    finishApartmentAssignment();
    setAssigningEstateId(null);
    toast.success(t('flatfinderWorkspaceApartmentAssigned'));
  }

  function startEstateConnection(estateId: string) {
    continueAfterDiscardingDrawing(() => {
      setSelectedId(null);
      setAssigningEstateId(estateId);
      setShowPanel(false);
      setHoveredEstateId(null);
    });
  }

  function disconnectEstate(estateId: string) {
    if (!perspective) return;
    replacePerspective({
      ...perspective,
      regions: perspective.regions.map((region) =>
        region.estateId === estateId ? { ...region, estateId: null } : region
      ),
    });
  }

  function toggleEstatePublication(targetEstate: FlatfinderEstate) {
    void runWorkspaceAction(async () => {
      await flatfinderRequest(
        `/estates/${targetEstate.id}/${targetEstate.status === 'published' ? 'unpublish' : 'publish'}`,
        'PUT'
      );
      acceptServerData(await getFlatfinder(data.projectId), !history.dirty);
      toast.success(
        targetEstate.status === 'published' ? t('apartmentUnpublished') : t('apartmentPublished')
      );
    });
  }

  function createEstateForSelectedRegion() {
    requestId.current = crypto.randomUUID();
    setEstateForm('new');
  }

  const perspectiveControls = (
    <FlatfinderPerspectiveControls
      busy={busy}
      canEdit={canEditLayout}
      perspective={perspective}
      perspectives={history.perspectives}
      onAdd={createPerspective}
      onEdit={editCurrentPerspective}
      onRemove={requestCurrentPerspectiveRemoval}
      onSelect={selectPerspective}
    />
  );

  if (!data.id) {
    return (
      <section className="flex flex-col items-start gap-4 rounded border bg-white p-6">
        <h1 className="text-2xl font-semibold">
          {t('flatfinderFormFlatfinder')} · {data.project.name}
        </h1>
        <p>{t('flatfinderWorkspaceIntro')}</p>
        <Button
          type="button"
          disabled={busy || !canEditLayout}
          onClick={() =>
            void runWorkspaceAction(async () =>
              acceptServerData(await initializeFlatfinder(data.projectId))
            )
          }
          variant="contained"
        >
          {t('flatfinderWorkspaceCreateFlatfinder')}
        </Button>
      </section>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 pb-6">
      {blocker.status === 'blocked' ? (
        <SimpleConfirmModal
          modal={{ isOpen: true, open: () => {}, dismiss: blocker.reset }}
          title={t('unsavedChanges')}
          body={t('flatfinderWorkspaceLeaveConfirm')}
          confirmButtonText={t('leavePage')}
          confirmButtonVariant="destructive"
          onConfirm={blocker.proceed}
        />
      ) : null}
      <SimpleConfirmModal
        modal={{
          isOpen: reloadConfirmation,
          open: () => setReloadConfirmation(true),
          dismiss: () => setReloadConfirmation(false),
        }}
        title={t('flatfinderWorkspaceReload')}
        body={t('flatfinderWorkspaceReloadConfirm')}
        confirmButtonText={t('flatfinderWorkspaceReload')}
        onConfirm={reloadLatestFlatfinder}
      />
      <SimpleConfirmModal
        modal={{
          isOpen: discardDrawingConfirmation,
          open: () => setDiscardDrawingConfirmation(true),
          dismiss: () => {
            actionAfterDiscardingDrawing.current = null;
            setDiscardDrawingConfirmation(false);
          },
        }}
        title={t('discardDrawingTitle')}
        body={t('discardDrawing')}
        confirmButtonText={t('discard')}
        confirmButtonVariant="destructive"
        onConfirm={discardCurrentDrawing}
      />
      <SimpleConfirmModal
        modal={{
          isOpen: removePerspectiveConfirmation !== null,
          open: () => {},
          dismiss: () => setRemovePerspectiveConfirmation(null),
        }}
        title={t('flatfinderWorkspaceRemovePerspective')}
        body={t('flatfinderWorkspaceRemoveConfirm')}
        confirmButtonText={t('flatfinderWorkspaceRemovePerspective')}
        confirmButtonVariant="destructive"
        onConfirm={removeConfirmedPerspective}
      />

      <FlatfinderWorkspaceHeader
        busy={busy}
        canDelete={data.permissions.general && data.permissions.media}
        projectName={data.project.name}
        onDelete={() => setDeleteConfirmation(true)}
        onPreview={() => setPreview(true)}
        onShare={() => setSettings(true)}
      />
      <DeleteFlatfinderModal
        busy={busy}
        open={deleteConfirmation}
        onConfirm={deleteFlatfinder}
        onDismiss={() => setDeleteConfirmation(false)}
      />
      <FlatfinderSettingsModal
        busy={busy}
        data={data}
        open={settings}
        saveError={settingsSaveError}
        onDismiss={() => {
          setSettings(false);
          setSettingsSaveError(null);
        }}
        onSave={saveSharingSettings}
      />
      <FlatfinderPreviewModal
        data={data}
        open={preview}
        perspectives={history.perspectives}
        onDismiss={() => setPreview(false)}
      />
      <FlatfinderPerspectiveModal
        form={perspectiveForm}
        projectId={data.projectId}
        uploading={uploading}
        onBusyChange={setUploading}
        onDismiss={() => setPerspectiveForm(null)}
        onSave={savePerspective}
      />
      <FlatfinderEstateModal
        busy={busy}
        data={data}
        estate={formEstate}
        form={estateForm}
        onDismiss={() => setEstateForm(null)}
        onSave={saveEstate}
        onTogglePublication={toggleEstatePublication}
      />

      <Button variant="outline" className="lg:hidden" onClick={() => setShowPanel(true)}>
        {t('apartments')}
      </Button>
      <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {perspective ? (
          <PolygonEditor
            key={`${perspective.id}-${perspective.imageId}-${canvasGeneration}`}
            perspective={perspective}
            selectedId={selectedId}
            hoveredEstateId={hoveredEstateId}
            onHoverEstate={setAreaHoveredEstateId}
            assigning={assigningEstateId !== null}
            onCancelAssignment={() => setAssigningEstateId(null)}
            onAssignArea={assignConnectingEstateToRegion}
            onSelect={selectRegion}
            onChange={(regions) => replacePerspective({ ...perspective, regions })}
            onError={(message) => toast.error(message)}
            perspectiveControls={perspectiveControls}
            onDrawingChange={setDrawing}
            onUndo={history.undo}
            onRedo={history.redo}
            canUndo={history.canUndo}
            canRedo={history.canRedo}
            saving={busy}
            onSave={saveEditorLayout}
            disabled={
              busy ||
              preview ||
              deleteConfirmation ||
              reloadConfirmation ||
              discardDrawingConfirmation ||
              removePerspectiveConfirmation !== null ||
              blocker.status === 'blocked' ||
              perspectiveForm !== null ||
              estateForm !== null ||
              settings ||
              !canEditLayout
            }
          />
        ) : (
          <EmptyFlatfinderEditor
            busy={busy}
            canEdit={canEditLayout}
            canRedo={history.canRedo}
            canSave={data.perspectives.length > 0}
            canUndo={history.canUndo}
            perspectiveControls={perspectiveControls}
            onRedo={history.redo}
            onSave={saveEditorLayout}
            onUndo={history.undo}
          />
        )}
        <FlatfinderApartmentPanel
          assignmentVersion={assignmentVersion}
          busy={busy}
          canAssign={canEditLayout}
          canConnectSelection={Boolean(selected && !estate)}
          canCreate={data.permissions.general && data.permissions.media}
          canCreateForSelection={Boolean(selected && !estate)}
          estates={data.estates}
          highlightedEstateId={areaHoveredEstateId ?? hoveredEstateId}
          panelVisible={showPanel}
          perspectiveId={perspective?.id}
          regions={perspective?.regions ?? []}
          selectedRegionId={selectedId}
          onClose={() => setShowPanel(false)}
          onConnect={assignSelectedRegionToEstate}
          onCreate={createEstateForSelectedRegion}
          onDisconnect={disconnectEstate}
          onEdit={setEstateForm}
          onHover={setHoveredEstateId}
          onSelect={selectRegion}
          onStartConnect={startEstateConnection}
        />
      </div>
    </div>
  );
}
